import type { PurchaseFn } from 'shared/payments/types';

// Structural types for Stripe context — avoids importing from modules
// with DOM/Preact side effects (stripe.tsx, auth/index.tsx), keeping
// this file testable in a Node environment without a browser.
export type CheckoutStripeCtx = {
  stripe: {
    confirmPayment(opts: any): Promise<{ error?: any }>;
    confirmSetup(opts: any): Promise<{ error?: any }>;
    confirmCardPayment(secret: string, opts?: any): Promise<{ paymentIntent?: any; error?: any }>;
  };
  elements: {
    submit(): Promise<{ error?: any }>;
    getElement(type: string): any;
  };
};

export type CheckoutUser = {
  name?: string;
};

/**
 * Build the confirmParams for stripe.confirmPayment/confirmSetup.
 * Payment Element always has address:'never' and name:'never', so we must
 * pass both in confirmParams. Uses Address Element value when available,
 * otherwise defaults to { country: 'US' }.
 *
 * This is the SINGLE source of truth for billing_details construction —
 * used by processStripePayment and upsell ProcessingForm.
 */
export function buildStripeConfirmParams(opts: {
  returnUrl: string;
  userName?: string;
  address?: Record<string, string | undefined>;
}) {
  const billing_details: Record<string, unknown> = {};
  if (opts.userName) {
    billing_details.name = opts.userName;
  }
  billing_details.address = opts.address || { country: 'US' };
  return {
    return_url: opts.returnUrl,
    payment_method_data: { billing_details },
  };
}

export function buildPaymentReturnUrl(priceId: string) {
  const url = new URL(window.location.href);
  url.searchParams.set('priceId', priceId);
  url.searchParams.set('payment_redirect', 'true');
  return url.toString();
}

export type DescribedCheckoutError = {
  kind: 'card' | 'other';
  message: string;
  code?: string;
};

const FRIENDLY_DECLINE_MESSAGES: Record<string, string> = {
  insufficient_funds: 'Your card has insufficient funds. Try a different card.',
  expired_card: 'Your card has expired. Use a different card.',
  incorrect_cvc: 'The security code is incorrect. Check the CVC on your card and try again.',
  invalid_cvc: 'The security code is incorrect. Check the CVC on your card and try again.',
  incorrect_number: 'The card number is incorrect. Double-check it and try again.',
  invalid_number: 'The card number is incorrect. Double-check it and try again.',
  incorrect_zip: 'The ZIP or postal code does not match your card. Check it and try again.',
  invalid_expiry_month: 'The expiration month is invalid. Check your card and try again.',
  invalid_expiry_year: 'The expiration year is invalid. Check your card and try again.',
  processing_error:
    'Something went wrong processing your card. Please try again in a moment, or use a different card.',
  try_again_later:
    'Your bank is temporarily unable to process this card. Please try again in a few minutes.',
  authentication_required:
    'Your bank declined the verification step. Please try again or use a different card.',
  card_not_supported: 'This card type is not supported. Please try a different card.',
  currency_not_supported:
    'This card cannot be used for the purchase currency. Try a different card.',
  do_not_honor: 'Your card was declined. Contact your bank, or try a different card.',
  generic_decline: 'Your card was declined. Contact your bank, or try a different card.',
  call_issuer: 'Your card was declined. Please contact your bank, or try a different card.',
  approve_with_id: 'Your card was declined. Please contact your bank, or try a different card.',
  card_velocity_exceeded:
    'This card has hit its transaction limit. Try again later or use a different card.',
  withdrawal_count_limit_exceeded:
    'This card has hit its transaction limit. Try again later or use a different card.',
  // For security, fraud / lost / stolen / pickup / restricted all surface as
  // a generic "use a different card" message — never reveal the bank's reason.
  fraudulent: 'Your card cannot be used for this purchase. Please try a different card.',
  lost_card: 'Your card cannot be used for this purchase. Please try a different card.',
  stolen_card: 'Your card cannot be used for this purchase. Please try a different card.',
  pickup_card: 'Your card cannot be used for this purchase. Please try a different card.',
  restricted_card: 'Your card cannot be used for this purchase. Please try a different card.',
};

const FALLBACK_CARD_MESSAGE =
  'Your card could not be processed. Please try a different card or contact your bank.';

/**
 * Map a thrown error from Stripe confirm flows or our server into a
 * user-facing message + display kind. `kind === 'card'` means the error
 * comes from the customer's card and should render inline near the payment
 * field. Anything else falls through to the generic toast handler.
 */
export function describeCheckoutError(err: unknown): DescribedCheckoutError {
  const e = err as
    | {
        type?: string;
        code?: string;
        decline_code?: string;
        message?: string;
        data?: { code?: string };
      }
    | undefined;
  if (!e || typeof e !== 'object') {
    return { kind: 'other', message: 'Something went wrong. Please try again.' };
  }
  if (e.data?.code === 'ALREADY_ACTIVE') {
    return {
      kind: 'card',
      message:
        "You've already purchased this with this email. Use a different email, or sign in to access your course.",
      code: 'ALREADY_ACTIVE',
    };
  }
  if (e.type === 'card_error') {
    const lookupCode = e.decline_code || e.code;
    const friendly = lookupCode ? FRIENDLY_DECLINE_MESSAGES[lookupCode] : undefined;
    return {
      kind: 'card',
      message: friendly || e.message || FALLBACK_CARD_MESSAGE,
      code: lookupCode,
    };
  }
  if (e.type === 'validation_error') {
    return {
      kind: 'card',
      message: e.message || 'Please check your card details and try again.',
      code: e.code,
    };
  }
  if (isLockTimeoutError(e)) {
    return {
      kind: 'card',
      message:
        'We hit a brief processing delay. Please wait a moment and try again — your card has not been charged.',
      code: e.code || 'lock_timeout',
    };
  }
  return {
    kind: 'other',
    message: e.message || 'Something went wrong. Please try again.',
    code: e.code,
  };
}

/**
 * Detect Stripe's transient lock_timeout / idempotency_error. Fires when a
 * client-side confirm call lands while Stripe's internal pipeline is still
 * finishing post-create work on the Customer/Invoice/PI graph (observed in
 * prod Apr 29 with the new charge-at-signup paymentplan flow). Stripe
 * documents this error as transient and explicitly retriable.
 */
export function isLockTimeoutError(err: unknown): boolean {
  if (!err || typeof err !== 'object') {
    return false;
  }
  const e = err as { code?: string; type?: string; message?: string };
  if (e.code === 'lock_timeout') {
    return true;
  }
  if (e.type === 'idempotency_error') {
    return true;
  }
  // Defensive substring match — the structured tag is sometimes missing on
  // older API versions or when the error surfaces through a layer that
  // strips fields. Gated on type=invalid_request_error so we never
  // accidentally retry a card decline whose Stripe message happens to mention
  // these phrases. Stripe classifies lock_timeout under invalid_request_error.
  if (e.type === 'invalid_request_error') {
    const msg = (e.message || '').toLowerCase();
    if (msg.includes('another api request') && msg.includes('currently accessing')) {
      return true;
    }
  }
  return false;
}

/**
 * Extract a sanitized, loggable view of a Stripe checkout error. PII-free —
 * intended for console/Sentry. Captures the codes we'll grep for when
 * debugging decline patterns. Never include the customer's email, name, or
 * card details.
 */
export function loggableCheckoutError(err: unknown): Record<string, unknown> {
  const e = err as
    | {
        type?: string;
        code?: string;
        decline_code?: string;
        message?: string;
        payment_intent?: { id?: string; status?: string };
        setup_intent?: { id?: string; status?: string };
      }
    | undefined;
  if (!e || typeof e !== 'object') {
    return { type: typeof err };
  }
  return {
    type: e.type,
    code: e.code,
    decline_code: e.decline_code,
    message: e.message,
    payment_intent_id: e.payment_intent?.id,
    payment_intent_status: e.payment_intent?.status,
    setup_intent_id: e.setup_intent?.id,
    setup_intent_status: e.setup_intent?.status,
  };
}

export const LOCK_TIMEOUT_RETRY_DELAY_MS = 1500;

/**
 * Run a Stripe confirm call with one retry on lock_timeout. Same call args
 * are replayed; Stripe treats confirm on a given clientSecret as idempotent —
 * if the first attempt actually succeeded but surfaced as an error, the
 * second call returns the existing succeeded intent without re-charging.
 *
 * Only retries lock_timeout. All other errors (card declines, validation,
 * etc.) propagate immediately so existing handling kicks in unchanged.
 *
 * `delayMs` is overridable for tests. Production code should never pass it.
 */
export async function confirmWithLockRetry<T extends { error?: unknown }>(
  attempt: () => Promise<T>,
  delayMs: number = LOCK_TIMEOUT_RETRY_DELAY_MS,
): Promise<T> {
  const first = await attempt();
  if (!first.error || !isLockTimeoutError(first.error)) {
    return first;
  }
  const e = first.error as { code?: string; type?: string };
  console.warn('checkout: stripe lock_timeout, retrying confirm', {
    code: e.code,
    type: e.type,
    delayMs,
  });
  await new Promise((r) => setTimeout(r, delayMs));
  const second = await attempt();
  if (second.error && isLockTimeoutError(second.error)) {
    const e2 = second.error as { code?: string; type?: string };
    console.warn('checkout: stripe lock_timeout persisted on retry', {
      code: e2.code,
      type: e2.type,
    });
  }
  return second;
}

/**
 * Core Stripe confirmation flow: purchase → handle result → confirm → re-purchase.
 * Shared by processStripePayment (main checkout) and upsell ProcessingForm.
 */
export async function confirmAndFinalizePurchase({
  ctx,
  purchase,
  userName,
  address,
  returnUrl,
}: {
  ctx: CheckoutStripeCtx;
  purchase: () => ReturnType<PurchaseFn>;
  userName?: string;
  address?: Record<string, string | undefined>;
  returnUrl: string;
}) {
  const result = await purchase();
  if (!result) {
    return;
  }

  if (result.type === 'success') {
    return result;
  }

  if (result.type === 'clientSecret') {
    const confirmParams = buildStripeConfirmParams({ returnUrl, userName, address });

    if (result.confirmType === 'setup') {
      const confirmResult = await confirmWithLockRetry(() =>
        ctx.stripe.confirmSetup({
          elements: ctx.elements,
          clientSecret: result.clientSecret,
          confirmParams,
          redirect: 'if_required',
        }),
      );
      if (confirmResult.error) {
        throw confirmResult.error;
      }
    } else {
      const confirmResult = await confirmWithLockRetry(() =>
        ctx.stripe.confirmPayment({
          elements: ctx.elements,
          clientSecret: result.clientSecret,
          confirmParams,
          redirect: 'if_required',
        }),
      );
      if (confirmResult.error) {
        throw confirmResult.error;
      }
    }

    return await purchase();
  }

  if (result.type === 'action') {
    const actionResult = await confirmWithLockRetry(() =>
      ctx.stripe.confirmCardPayment(result.action, {
        setup_future_usage: 'off_session',
      }),
    );
    // Match the throw-on-error contract used by the confirmPayment / confirmSetup
    // branches above so the outer catch routes through describeCheckoutError.
    // Without this, a lock_timeout (or any other error) on the legacy 3DS path
    // returns silently and the user sees the spinner end with no feedback.
    if (actionResult.error) {
      throw actionResult.error;
    }
    if (!actionResult.paymentIntent) {
      console.error(actionResult);
      return;
    }
    return await purchase();
  }
}

export async function processStripePayment({
  ctx,
  user,
  priceId,
  couponId,
  processPayment,
  customAmountInCents,
  returnUrl,
  fallbackAddress,
}: {
  ctx: CheckoutStripeCtx;
  user?: CheckoutUser;
  processPayment: PurchaseFn;
  priceId: string;
  couponId?: string;
  customAmountInCents?: number;
  returnUrl?: string;
  /**
   * Billing address to use when the form has no address element (e.g. the AIP
   * join forms skip it when one is already on file). The Payment Element opts
   * out of address fields, so Stripe requires an address in confirmParams —
   * without one, confirmSetup/confirmPayment reject the submission.
   */
  fallbackAddress?: Record<string, string | undefined>;
}) {
  const addressElement = ctx.elements.getElement('address');

  const submitResult = await ctx.elements.submit();
  if (submitResult.error) {
    if (submitResult.error.type !== 'validation_error') {
      throw submitResult.error;
    }
    return { type: 'validationError' };
  }

  let address: Record<string, string | undefined> | undefined = fallbackAddress;

  if (addressElement) {
    const addressFormState = await addressElement.getValue();
    if (!addressFormState.complete) {
      return { type: 'validationError' };
    }
    address = {
      ...addressFormState.value.address,
      line2: addressFormState.value.address?.line2 || undefined,
    };
  }

  const purchase = () =>
    processPayment({
      priceId,
      couponId,
      ...(address ? { address } : {}),
      customAmountInCents,
    });

  return confirmAndFinalizePurchase({
    ctx,
    purchase,
    userName: user?.name,
    address,
    returnUrl: returnUrl || buildPaymentReturnUrl(priceId),
  });
}
